import React, { useState, useEffect, useRef } from "react";
import { Drawer, Input, Select, Dropdown } from "antd";
import { CloseOutlined, DownOutlined, EllipsisOutlined } from "@ant-design/icons";
import "./FiltersDrawer.css";

const PRICE_MIN = 0;
const PRICE_MAX = 100000;
const PRICE_STEP = 1000;
const YEAR_MIN = 1990;
const YEAR_MAX = new Date().getFullYear() + 1;
const MILEAGE_MIN = 0;
const MILEAGE_MAX = 999999;

const COMMON_MAKES = [
  "Acura", "Audi", "BMW", "Buick", "Cadillac", "Chevrolet", "Chrysler", "Ford",
  "GMC", "Honda", "Hyundai", "Infiniti", "Jeep", "Kia", "Lexus", "Mazda",
  "Mercedes-Benz", "Nissan", "Ram", "Subaru", "Toyota", "Volkswagen", "Volvo",
];

const EMPTY_STATE = {
  makes: [],
  yearMin: null,
  yearMax: null,
  priceMin: null,
  priceMax: null,
  mileageMin: null,
  mileageMax: null,
  radius: null,
  listingId: null,
};

function formatPrice(n) {
  if (n == null || n === "") return "";
  const num = Number(n);
  return isNaN(num) ? "" : `$${num.toLocaleString()}`;
}

function parsePrice(str) {
  const cleaned = String(str).replace(/[$,]/g, "").trim();
  const num = parseInt(cleaned, 10);
  return isNaN(num) ? null : Math.max(PRICE_MIN, Math.min(PRICE_MAX, num));
}

export default function FiltersDrawer({
  open,
  onClose,
  initialFilters = {},
  defaultFilters = null,
  makesOptions = null,
  onApply,
  onResetToDefault,
  onClearDefault,
  onSaveDefault,
  matchCount = 0,
}) {
  const makesList = Array.isArray(makesOptions) && makesOptions.length > 0 ? makesOptions : COMMON_MAKES;

  const norm = (f) => ({
    makes: Array.isArray(f.makes) ? f.makes : [],
    yearMin: f.yearMin != null ? f.yearMin : null,
    yearMax: f.yearMax != null ? f.yearMax : null,
    priceMin: f.priceMin != null ? f.priceMin : null,
    priceMax: f.priceMax != null ? f.priceMax : null,
    mileageMin: f.mileageMin != null ? f.mileageMin : null,
    mileageMax: f.mileageMax != null ? f.mileageMax : null,
    radius: f.radius != null ? f.radius : null,
    listingId: f.listingId != null && String(f.listingId).trim() !== "" ? String(f.listingId).trim() : null,
  });

  const [filters, setFilters] = useState(() => norm(initialFilters));
  const [makeSearch, setMakeSearch] = useState("");
  const [makeDropdownOpen, setMakeDropdownOpen] = useState(false);
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) setFilters(norm(initialFilters));
    prevOpenRef.current = !!open;
  }, [open, initialFilters]);

  const filteredMakes = makesList.filter(
    (m) => !filters.makes.includes(m) && String(m).toLowerCase().includes((makeSearch || "").toLowerCase())
  );

  const handleReset = () => {
    setFilters(defaultFilters ? norm(defaultFilters) : EMPTY_STATE);
  };

  const handleClearAll = () => {
    setFilters(EMPTY_STATE);
    onApply(EMPTY_STATE);
    onClose();
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const addMake = (make) => {
    if (!filters.makes.includes(make)) setFilters((prev) => ({ ...prev, makes: [...prev.makes, make] }));
    setMakeSearch("");
    setMakeDropdownOpen(false);
  };

  const removeMake = (make) => {
    setFilters((prev) => ({ ...prev, makes: prev.makes.filter((m) => m !== make) }));
  };

  const defaultMenuItems = [
    defaultFilters && { key: "useDefault", label: "Use default", onClick: () => { handleReset(); onResetToDefault?.(); } },
    defaultFilters && { key: "clearDefault", label: "Clear default", onClick: () => onClearDefault?.() },
  ].filter(Boolean);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  return (
    <Drawer
      title={null}
      placement="right"
      onClose={onClose}
      open={open}
      width={isMobile ? "100%" : 420}
      closable={false}
      className="bs-filters-drawer"
      styles={{
        header: { display: "none" },
        body: {
          padding: 0,
          height: isMobile ? "100%" : "100vh",
          maxHeight: isMobile ? "100dvh" : undefined,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
        mask: { backdropFilter: "blur(18px)" },
        content: {},
        wrapper: {},
      }}
    >
      <div className="bs-filters-drawer-wrapper">
        <header className="bs-filters-drawer-header">
          <h2 className="bs-filters-drawer-title">Filters</h2>
          <div className="bs-filters-drawer-header-actions">
            <button type="button" className="bs-filters-drawer-reset" onClick={handleReset}>
              Reset
            </button>
            {defaultMenuItems.length > 0 && (
              <Dropdown menu={{ items: defaultMenuItems }} trigger={["click"]}>
                <button type="button" className="bs-filters-drawer-kebab" aria-label="Default filters options">
                  <EllipsisOutlined />
                </button>
              </Dropdown>
            )}
            <button type="button" className="bs-filters-drawer-close" onClick={onClose} aria-label="Close">
              <CloseOutlined />
            </button>
          </div>
        </header>

        <div className="bs-filters-drawer-body">
          <section className="bs-filters-section">
            <h3 className="bs-filters-section-title">Distance</h3>
            <div className="bs-filters-field">
              <label className="bs-filters-label">Within radius</label>
              <Select
                className="bs-filters-radius-select"
                dropdownClassName="bs-filters-radius-dropdown"
                placeholder="All"
                value={filters.radius != null ? filters.radius : undefined}
                allowClear
                onClear={() => setFilters((prev) => ({ ...prev, radius: null }))}
                onChange={(v) => setFilters((prev) => ({ ...prev, radius: v ?? null }))}
                options={[
                  { value: 10, label: "10 mi" },
                  { value: 25, label: "25 mi" },
                  { value: 50, label: "50 mi" },
                  { value: 100, label: "100 mi" },
                  { value: 150, label: "150 mi" },
                  { value: 200, label: "200 mi" },
                ]}
                suffixIcon={<DownOutlined />}
              />
            </div>
          </section>

          <section className="bs-filters-section">
            <h3 className="bs-filters-section-title">Vehicle</h3>

            <div className="bs-filters-field">
              <label className="bs-filters-label">Makes</label>
              <div
                className={`bs-filters-makes-wrap ${makeDropdownOpen ? "bs-filters-makes-wrap--focused" : ""}`}
                onClick={() => setMakeDropdownOpen(true)}
              >
                <div className="bs-filters-makes-inner">
                  {filters.makes.map((m) => (
                    <span key={m} className="bs-filters-make-chip">
                      {m}
                      <button type="button" className="bs-filters-make-chip-remove" aria-label={`Remove ${m}`} onClick={(e) => { e.stopPropagation(); removeMake(m); }}><CloseOutlined /></button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="bs-filters-makes-input"
                    placeholder={filters.makes.length ? "" : "Search makes…"}
                    value={makeSearch}
                    onChange={(e) => setMakeSearch(e.target.value)}
                    onFocus={() => setMakeDropdownOpen(true)}
                  />
                </div>
                <DownOutlined className="bs-filters-makes-caret" />
                {makeDropdownOpen && (
                  <>
                    <div className="bs-filters-makes-backdrop" onClick={(e) => { e.stopPropagation(); setMakeDropdownOpen(false); }} />
                    <div className="bs-filters-makes-dropdown">
                      {filteredMakes.length === 0 ? (
                        <div className="bs-filters-makes-dropdown-empty">No makes match</div>
                      ) : (
                        filteredMakes.slice(0, 50).map((m) => (
                          <button key={m} type="button" className="bs-filters-makes-option" onClick={() => addMake(m)}>{m}</button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="bs-filters-field-divider" />
            <div className="bs-filters-field">
              <label className="bs-filters-label">Year Range</label>
              <div className="bs-filters-range-inputs">
                <Input
                  className="bs-filters-input"
                  placeholder={String(YEAR_MIN)}
                  value={filters.yearMin != null ? String(filters.yearMin) : ""}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                    const n = v === "" ? null : parseInt(v, 10);
                    setFilters((prev) => ({ ...prev, yearMin: isNaN(n) ? null : n }));
                  }}
                />
                <span className="bs-filters-range-sep">—</span>
                <Input
                  className="bs-filters-input"
                  placeholder={String(YEAR_MAX)}
                  value={filters.yearMax != null ? String(filters.yearMax) : ""}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                    const n = v === "" ? null : parseInt(v, 10);
                    setFilters((prev) => ({ ...prev, yearMax: isNaN(n) ? null : n }));
                  }}
                />
              </div>
            </div>

            <div className="bs-filters-field-divider" />
            <div className="bs-filters-field">
              <label className="bs-filters-label">Price Range</label>
              <div className="bs-filters-range-inputs">
                <Input
                  className="bs-filters-input"
                  placeholder="$0"
                  value={formatPrice(filters.priceMin)}
                  onChange={(e) => {
                    const n = parsePrice(e.target.value);
                    setFilters((prev) => ({ ...prev, priceMin: n ?? null }));
                  }}
                />
                <span className="bs-filters-range-sep">—</span>
                <Input
                  className="bs-filters-input"
                  placeholder="$100,000"
                  value={formatPrice(filters.priceMax)}
                  onChange={(e) => {
                    const n = parsePrice(e.target.value);
                    setFilters((prev) => ({ ...prev, priceMax: n ?? null }));
                  }}
                />
              </div>
            </div>

            <div className="bs-filters-field-divider" />
            <div className="bs-filters-field">
              <label className="bs-filters-label">Mileage</label>
              <div className="bs-filters-range-inputs">
                <Input
                  className="bs-filters-input"
                  placeholder="0"
                  value={filters.mileageMin != null ? Number(filters.mileageMin).toLocaleString() : ""}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "").replace(/\D/g, "");
                    const num = raw === "" ? null : Math.min(MILEAGE_MAX, parseInt(raw, 10));
                    setFilters((prev) => ({ ...prev, mileageMin: num }));
                  }}
                />
                <span className="bs-filters-range-sep">—</span>
                <Input
                  className="bs-filters-input"
                  placeholder="120,000"
                  value={filters.mileageMax != null ? Number(filters.mileageMax).toLocaleString() : ""}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, "").replace(/\D/g, "");
                    const num = raw === "" ? null : Math.min(MILEAGE_MAX, parseInt(raw, 10));
                    setFilters((prev) => ({ ...prev, mileageMax: num }));
                  }}
                />
              </div>
            </div>
          </section>

          <section className="bs-filters-section bs-filters-section--prefs">
            <h3 className="bs-filters-section-title">PREFERENCES</h3>
            <div className="bs-filters-prefs-row">
              <span className="bs-filters-prefs-text">
                {defaultFilters ? "Default filters saved" : "Save these filters as default"}
              </span>
              {defaultFilters ? (
                <div className="bs-filters-prefs-btns">
                  <button type="button" className="bs-filters-clear-default-btn" onClick={() => onClearDefault?.()}>
                    Clear Default Filters
                  </button>
                  <button type="button" className="bs-filters-save-default-btn" onClick={() => onSaveDefault?.(filters)}>
                    Update Default Filters
                  </button>
                </div>
              ) : (
                <button type="button" className="bs-filters-save-default-btn" onClick={() => onSaveDefault?.(filters)}>
                  Save as Default
                </button>
              )}
            </div>
          </section>
        </div>

        <footer className="bs-filters-drawer-footer">
          <button type="button" className="bs-filters-clear-all" onClick={handleClearAll}>
            Clear All
          </button>
          <button type="button" className="bs-filters-apply-btn" onClick={handleApply}>
            Apply Filters
            {matchCount > 0 && <span className="bs-filters-apply-count">{matchCount}</span>}
          </button>
        </footer>
      </div>
    </Drawer>
  );
}
